import React, { useRef, useState, useEffect } from "react";
import { Button, Modal, Select, Input } from "./ui";
import { TL } from "../i18n";
import client, { apiErr } from "../lib/api";
import { Upload, Download, Loader2, FileSpreadsheet, Scale, Plus, Trash2, Pencil, Check, X, Braces } from "lucide-react";

const APP_LABELS = { currency: "Currency pools", holdings: "Holdings", benefits: "Benefits", family_tree: "Family Tree people", liabilities: "Liabilities" };

// Import Holdings / Currency / Benefits / People / Liabilities records from a CSV or Excel file.
// Two steps: parse (detect columns + suggest mapping) -> commit (create records).
export function ImportButton({ app, label = null, accent = null, extra = null, onDone }) {
  if (label == null) label = TL("Import CSV/Excel");
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [parsed, setParsed] = useState(null); // {columns, rows, fields, suggested, count}
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState("");

  // Custodian templates are shaped for Holdings; load them so a custodian export auto-maps its columns.
  useEffect(() => {
    if (app !== "holdings") return;
    client.get("/import-templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
  }, [app]);

  // Apply a custodian template's column map to the current mapping, keeping only targets this
  // importer knows and source columns actually present in the uploaded file.
  const applyTemplate = (key) => {
    setTpl(key);
    const tp = templates.find((x) => x.key === key);
    if (!tp || !parsed) return;
    const fieldKeys = new Set((parsed.fields || []).map((f) => f.key));
    const cols = new Set(parsed.columns || []);
    const next = { ...(parsed.suggested || {}) };
    (tp.mapping || []).forEach(({ source, target }) => {
      if (fieldKeys.has(target) && cols.has(source)) next[target] = source;
    });
    setMapping(next);
  };

  const reset = () => { setParsed(null); setMapping({}); setResult(null); setErr(""); setTpl(""); };
  const close = () => { setOpen(false); reset(); };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await client.post(`/import/${app}/parse`, fd);
      setParsed(data);
      setMapping(data.suggested || {});
      setOpen(true);
    } catch (e2) { setErr(apiErr(e2)); setOpen(true); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const commit = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await client.post(`/import/${app}/commit`, { mapping, rows: parsed.rows, extra: extra || {} });
      setResult(data);
    } catch (e2) { setErr(apiErr(e2)); }
    setBusy(false);
  };

  const mappedPreview = (parsed?.rows || []).slice(0, 5);
  const requiredOk = (parsed?.fields || []).filter((f) => f.required).every((f) => mapping[f.key]);

  return (
    <>
      <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm,.ofx,.qfx" className="hidden" onChange={onPick} data-testid={`import-${app}-file`} />
      <Button variant="ghost" onClick={() => fileRef.current?.click()} disabled={busy}
        style={accent ? { color: accent } : undefined} data-testid={`import-${app}-btn`}>
        {busy && !open ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {label}
      </Button>

      <Modal open={open} onClose={close} title={`Import — ${APP_LABELS[app] || "records"}`} testid={`import-${app}-modal`} wide>
        {err && <div className="mb-3 rounded-md bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-700 dark:text-red-300" data-testid={`import-${app}-error`}>{err}</div>}

        {result ? (
          <div className="space-y-3" data-testid={`import-${app}-result`}>
            <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/40 px-3 py-3 text-sm text-emerald-800 dark:text-emerald-300">
              Imported <b>{result.created}</b> record{result.created === 1 ? "" : "s"}
              {result.auto_categorized ? <> · auto-categorized <b>{result.auto_categorized}</b> blank{result.auto_categorized === 1 ? "" : "s"}</> : null}
              {result.skipped ? <> · skipped <b>{result.skipped}</b> row{result.skipped === 1 ? "" : "s"} (missing name)</> : null}.
            </div>
            <div className="flex justify-end">
              <Button onClick={() => { const d = onDone; close(); d && d(); }} data-testid={`import-${app}-done`}>{TL("Done — refresh list")}</Button>
            </div>
          </div>
        ) : parsed ? (
          <div className="space-y-4">
            <p className="text-sm opacity-80">{TL("Found")} <b>{parsed.count}</b> row{parsed.count === 1 ? "" : "s"} and {parsed.columns.length} columns. Match each field to a column, then import.</p>
            {app === "holdings" && templates.length ? (
              <div className="rounded-md border border-[#C99415]/30 bg-[#C99415]/5 p-3" data-testid="import-holdings-custodian">
                <label className="mb-1 block text-xs font-medium opacity-70">{TL("Custodian template — auto-map your export's columns")}</label>
                <div className="flex items-center gap-2">
                  <Select value={tpl} onChange={(e) => applyTemplate(e.target.value)} data-testid="import-holdings-custodian-select" className="flex-1">
                    <option value="">{TL("Generic — match manually")}</option>
                    {templates.filter((x) => x.key !== "generic").map((x) => <option key={x.key} value={x.key}>{x.custodian}</option>)}
                  </Select>
                </div>
                {tpl ? <p className="mt-1 text-[11px] opacity-60">{templates.find((x) => x.key === tpl)?.description} Columns auto-mapped where they match — review below before importing.</p> : null}
              </div>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              {parsed.fields.map((f) => (
                <div key={f.key}>
                  <Select label={`${f.label}${f.required ? " *" : ""}`} value={mapping[f.key] || ""}
                    onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))}
                    data-testid={`import-${app}-map-${f.key}`}>
                    <option value="">{TL("— skip —")}</option>
                    {parsed.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto rounded-md border border-black/10 dark:border-white/10">
              <table className="w-full text-xs">
                <thead className="bg-black/5 dark:bg-white/10">
                  <tr>{parsed.fields.map((f) => <th key={f.key} className="px-2 py-1.5 text-start font-medium">{f.label}</th>)}</tr>
                </thead>
                <tbody>
                  {mappedPreview.map((row, i) => (
                    <tr key={i} className="border-t border-black/5 dark:border-white/5">
                      {parsed.fields.map((f) => <td key={f.key} className="px-2 py-1.5 whitespace-nowrap">{mapping[f.key] ? (row[mapping[f.key]] ?? "") : <span className="opacity-40">—</span>}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {mappedPreview.length ? <p className="text-xs opacity-60">Preview of first {mappedPreview.length} row(s).</p> : null}

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={close}>{TL("Cancel")}</Button>
              <Button onClick={commit} disabled={busy || !requiredOk} data-testid={`import-${app}-commit`}>
                {busy ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />} Import {parsed.count} row{parsed.count === 1 ? "" : "s"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-6 text-center text-sm opacity-70"><Loader2 size={18} className="mx-auto animate-spin" /> {TL("Reading file…")}</div>
        )}
      </Modal>
    </>
  );
}

// One-click portable export (CSV or JSON) for a single app collection. For anyone who prefers a
// file over cluster sync. `coll` is the backend collection (benefits/currencies/holdings/liabilities/persons).
export function ExportButton({ coll, label = null, filename = null, accent = null }) {
  if (label == null) label = TL("Export");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // XML types map to the collection name for these apps.
  const XML_TYPE = { benefits: "benefits", currencies: "currencies", holdings: "holdings",
                     liabilities: "liabilities", persons: "persons" };

  const download = async (fmt) => {
    setBusy(true);
    try {
      const isXml = fmt === "xml";
      const url = isXml
        ? `/export/xml?type=${XML_TYPE[coll] || coll}&pretty=1`
        : `/export/${coll}.${fmt}`;
      const res = await client.get(url, { responseType: "blob" });
      const blob = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blob; a.download = `${filename || coll}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blob);
    } catch (e) { /* noop */ }
    setBusy(false); setOpen(false);
  };

  return (
    <div className="relative inline-block" data-testid={`export-${coll}`}>
      <Button variant="ghost" onClick={() => setOpen((v) => !v)} disabled={busy}
        style={accent ? { color: accent } : undefined} data-testid={`export-${coll}-btn`}>
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} {label}
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 min-w-[9rem] overflow-hidden rounded-md border border-black/10 bg-white shadow-lg dark:border-white/10 dark:bg-neutral-900" data-testid={`export-${coll}-menu`}>
            <button onClick={() => download("csv")} className="flex w-full items-center gap-2 px-3 py-2 text-start text-sm hover:bg-black/5 dark:hover:bg-white/10" data-testid={`export-${coll}-csv`}><FileSpreadsheet size={14} /> {TL("CSV (spreadsheet)")}</button>
            <button onClick={() => download("json")} className="flex w-full items-center gap-2 px-3 py-2 text-start text-sm hover:bg-black/5 dark:hover:bg-white/10" data-testid={`export-${coll}-json`}><Braces size={14} /> {TL("JSON (portable)")}</button>
            <button onClick={() => download("xml")} className="flex w-full items-center gap-2 px-3 py-2 text-start text-sm hover:bg-black/5 dark:hover:bg-white/10" data-testid={`export-${coll}-xml`}><Braces size={14} /> {TL("XML (regulatory)")}</button>
          </div>
        </>
      )}
    </div>
  );
}

// Download the branded Consolidated Net-Worth Statement PDF (assets roll-up).
export function NetWorthButton({ accent = null }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await client.get("/networth-statement.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Consolidated_Net_Worth_Statement.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { /* noop */ }
    setBusy(false);
  };
  return (
    <Button variant="ghost" onClick={download} disabled={busy} style={accent ? { color: accent } : undefined} data-testid="networth-statement-btn">
      {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} {TL("Net-worth statement (PDF)")}
    </Button>
  );
}


// One-click Regulatory Bundle: ZIP with XML+JSON per collection + branded PDF + SHA-256 manifest.
export function RegulatoryBundleButton({ accent = null }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [types, setTypes] = useState(["holdings", "currencies", "liabilities", "persons"]);
  const [includePdf, setIncludePdf] = useState(true);
  const [available, setAvailable] = useState([]);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    client.get("/export/xml/types").then((r) => setAvailable(r.data?.types || [])).catch(() => {});
  }, [open]);

  const download = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const qs = new URLSearchParams({ types: types.join(","), include_networth_pdf: includePdf ? "1" : "0" });
      const res = await client.get(`/export/regulatory-bundle?${qs.toString()}`, { responseType: "blob" });
      const bid = res.headers["x-bundle-id"] || "";
      const files = res.headers["x-bundle-files"] || "?";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `regulatory-bundle-${bid}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setResult({ bundle_id: bid, files });
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };

  return (
    <>
      <Button variant="ghost" onClick={() => setOpen(true)} disabled={busy}
        style={accent ? { color: accent } : undefined} data-testid="regulatory-bundle-btn">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Braces size={15} />} {TL("Regulatory bundle (ZIP)")}
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} title={TL("Regulatory bundle")} testid="regulatory-bundle-modal">
        {err && <div className="mb-2 rounded-md bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-700 dark:text-red-300" data-testid="regulatory-bundle-error">{err}</div>}
        {result ? (
          <div className="space-y-2" data-testid="regulatory-bundle-result">
            <div className="rounded-md border border-[#24A148]/40 bg-[#24A148]/10 p-3 text-sm">
              <div className="font-medium text-[#24A148]">✓ Bundle downloaded</div>
              <div className="mt-1 text-xs opacity-70">Bundle ID <b>{result.bundle_id}</b> · {result.files} files inside · every file SHA-256 hashed in <code>MANIFEST.txt</code>.</div>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => { setResult(null); setOpen(false); }} data-testid="regulatory-bundle-done">{TL("Done")}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm opacity-70">{TL("A single ZIP that ships every requested collection as XML + JSON, plus a branded PDF and a SHA-256 manifest so a regulator can verify integrity offline.")}</p>
            <div className="space-y-1" data-testid="regulatory-bundle-types">
              <div className="text-sm font-medium">{TL("Include collections")}</div>
              <div className="flex flex-wrap gap-2">
                {(available.length ? available : [{ type: "holdings", records: "?" }, { type: "currencies", records: "?" }, { type: "liabilities", records: "?" }, { type: "persons", records: "?" }, { type: "benefits", records: "?" }, { type: "entities", records: "?" }]).map((t) => (
                  <label key={t.type} className={`flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${types.includes(t.type) ? "border-[var(--primary)] bg-[var(--primary)]/5" : "border-black/10 dark:border-white/10"}`}>
                    <input type="checkbox" checked={types.includes(t.type)} data-testid={`regulatory-bundle-type-${t.type}`}
                      onChange={(e) => setTypes(e.target.checked ? [...types, t.type] : types.filter((x) => x !== t.type))} />
                    {t.type} <span className="opacity-50">({t.records})</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={includePdf} onChange={(e) => setIncludePdf(e.target.checked)}
                data-testid="regulatory-bundle-pdf" />
              {TL("Include branded Net-Worth Statement PDF (recipient-watermarked)")}
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setOpen(false)} data-testid="regulatory-bundle-cancel">{TL("Cancel")}</Button>
              <Button onClick={download} disabled={busy || types.length === 0} data-testid="regulatory-bundle-download">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} {TL("Download bundle")}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

// Currencida liabilities: a simple list of what the estate owes, feeding the net-worth statement.
export function LiabilitiesManager() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ name: "", category: "", amount: "", rate: "1" });
  const [editId, setEditId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", category: "", amount: "", rate: "1" });

  const load = async () => {
    try { const { data } = await client.get("/liabilities"); setRows(data || []); } catch { /* noop */ }
  };
  const openModal = async () => { setOpen(true); await load(); };
  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0) * (Number(r.rate) || 1), 0);

  const add = async () => {
    if (!draft.name.trim()) return;
    setBusy(true);
    try {
      await client.post("/liabilities", { name: draft.name, category: draft.category, amount: Number(draft.amount) || 0, rate: Number(draft.rate) || 1 });
      setDraft({ name: "", category: "", amount: "", rate: "1" });
      await load();
    } catch { /* noop */ }
    setBusy(false);
  };
  const remove = async (id) => { setBusy(true); try { await client.delete(`/liabilities/${id}`); await load(); } catch { /* noop */ } setBusy(false); };
  const startEdit = (r) => { setEditId(r.id); setEditDraft({ name: r.name || "", category: r.category || "", amount: String(r.amount ?? ""), rate: String(r.rate ?? "1") }); };
  const cancelEdit = () => { setEditId(null); };
  const saveEdit = async (id) => {
    if (!editDraft.name.trim()) return;
    setBusy(true);
    try {
      await client.put(`/liabilities/${id}`, { name: editDraft.name, category: editDraft.category, amount: Number(editDraft.amount) || 0, rate: Number(editDraft.rate) || 1 });
      setEditId(null);
      await load();
    } catch { /* noop */ }
    setBusy(false);
  };

  return (
    <>
      <Button variant="ghost" onClick={openModal} data-testid="liabilities-open-btn"><Scale size={15} /> {TL("Liabilities")}</Button>
      <Modal open={open} onClose={() => setOpen(false)} title={TL("Currencida — Liabilities")} testid="liabilities-modal" wide>
        <p className="mb-3 text-sm opacity-80">{TL("What the estate owes. These are subtracted from assets in the Consolidated Net-Worth Statement.")}</p>

        <div className="mb-4 grid gap-2 sm:grid-cols-5" data-testid="liability-add-row">
          <Input label={TL("Name *")} value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} data-testid="liability-name" />
          <Input label={TL("Category")} value={draft.category} onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))} data-testid="liability-category" />
          <Input label={TL("Amount")} type="number" value={draft.amount} onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value }))} data-testid="liability-amount" />
          <Input label={TL("Rate to base")} type="number" value={draft.rate} onChange={(e) => setDraft((d) => ({ ...d, rate: e.target.value }))} data-testid="liability-rate" />
          <div className="flex items-end"><Button onClick={add} disabled={busy || !draft.name.trim()} data-testid="liability-add-btn"><Plus size={15} /> {TL("Add")}</Button></div>
        </div>

        <div className="overflow-x-auto rounded-md border border-black/10 dark:border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-black/5 dark:bg-white/10">
              <tr>
                <th className="px-2 py-1.5 text-start font-medium">{TL("Liability")}</th>
                <th className="px-2 py-1.5 text-start font-medium">{TL("Category")}</th>
                <th className="px-2 py-1.5 text-end font-medium">{TL("Amount")}</th>
                <th className="px-2 py-1.5 text-end font-medium">{TL("Rate")}</th>
                <th className="px-2 py-1.5 text-end font-medium">{TL("Base value")}</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody data-testid="liabilities-list">
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="px-2 py-3 text-center opacity-50">{TL("No liabilities yet.")}</td></tr>
              ) : rows.map((r) => (
                editId === r.id ? (
                  <tr key={r.id} className="border-t border-black/5 bg-blue-50/50 dark:border-white/5 dark:bg-blue-950/20" data-testid={`liability-row-${r.id}`}>
                    <td className="px-2 py-1"><input value={editDraft.name} onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))} className="w-full rounded border border-black/15 bg-transparent px-1.5 py-1 dark:border-white/15" data-testid={`liability-edit-name-${r.id}`} /></td>
                    <td className="px-2 py-1"><input value={editDraft.category} onChange={(e) => setEditDraft((d) => ({ ...d, category: e.target.value }))} className="w-full rounded border border-black/15 bg-transparent px-1.5 py-1 dark:border-white/15" data-testid={`liability-edit-category-${r.id}`} /></td>
                    <td className="px-2 py-1"><input type="number" value={editDraft.amount} onChange={(e) => setEditDraft((d) => ({ ...d, amount: e.target.value }))} className="w-24 rounded border border-black/15 bg-transparent px-1.5 py-1 text-end dark:border-white/15" data-testid={`liability-edit-amount-${r.id}`} /></td>
                    <td className="px-2 py-1"><input type="number" value={editDraft.rate} onChange={(e) => setEditDraft((d) => ({ ...d, rate: e.target.value }))} className="w-16 rounded border border-black/15 bg-transparent px-1.5 py-1 text-end dark:border-white/15" data-testid={`liability-edit-rate-${r.id}`} /></td>
                    <td className="px-2 py-1.5 text-end opacity-60">{((Number(editDraft.amount) || 0) * (Number(editDraft.rate) || 1)).toLocaleString()}</td>
                    <td className="px-2 py-1.5 text-end whitespace-nowrap">
                      <button onClick={() => saveEdit(r.id)} disabled={busy || !editDraft.name.trim()} className="text-emerald-600 hover:opacity-70 disabled:opacity-30" data-testid={`liability-save-${r.id}`}><Check size={16} /></button>
                      <button onClick={cancelEdit} className="ms-2 text-black/50 hover:opacity-70 dark:text-white/50" data-testid={`liability-cancel-${r.id}`}><X size={16} /></button>
                    </td>
                  </tr>
                ) : (
                <tr key={r.id} className="border-t border-black/5 dark:border-white/5" data-testid={`liability-row-${r.id}`}>
                  <td className="px-2 py-1.5">{r.name}</td>
                  <td className="px-2 py-1.5">{r.category}</td>
                  <td className="px-2 py-1.5 text-end">{(Number(r.amount) || 0).toLocaleString()}</td>
                  <td className="px-2 py-1.5 text-end">{r.rate}</td>
                  <td className="px-2 py-1.5 text-end">{((Number(r.amount) || 0) * (Number(r.rate) || 1)).toLocaleString()}</td>
                  <td className="px-2 py-1.5 text-end whitespace-nowrap">
                    <button onClick={() => startEdit(r)} className="text-blue-600 hover:opacity-70" data-testid={`liability-edit-${r.id}`}><Pencil size={15} /></button>
                    <button onClick={() => remove(r.id)} className="ms-2 text-red-600 hover:opacity-70" data-testid={`liability-del-${r.id}`}><Trash2 size={15} /></button>
                  </td>
                </tr>
                )
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot><tr className="border-t border-black/10 dark:border-white/10 font-medium">
                <td className="px-2 py-1.5" colSpan={4}>{TL("Total liabilities (base)")}</td>
                <td className="px-2 py-1.5 text-end" data-testid="liabilities-total">{total.toLocaleString()}</td><td></td>
              </tr></tfoot>
            )}
          </table>
        </div>
      </Modal>
    </>
  );
}
